/**
 * i18n catalog — default and only locale: es-PA (§9).
 * All UI copy lives here; no hard-coded strings in components.
 */
export const esPA = {
    common: {
        appName: 'Cumpli',
        loading: 'Cargando…',
        save: 'Guardar',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        search: 'Buscar',
        actions: 'Acciones',
        requiredField: 'Este campo es obligatorio',
        genericError: 'Ocurrió un error. Intente de nuevo.',
        forbidden: 'No tiene permisos para realizar esta acción.',
        notFound: 'Recurso no encontrado.',
        localBackendUnreachable: 'No se pudo conectar con el backend local.',
        localBackendUnreachableHelp: 'Verifica que el API local (puerto 8787) esté corriendo.',
    },
    auth: {
        login: 'Iniciar sesión',
        email: 'Correo',
        password: 'Contraseña',
        signIn: 'Ingresar',
        forgotPassword: '¿Olvidó su contraseña?',
        mfaTitle: 'Verificación en dos pasos',
        mfaCode: 'Código de verificación',
        mfaHelp: 'Abre tu aplicación de autenticación (Google Authenticator, Authy, Microsoft Authenticator) y escribe el código de 6 dígitos que aparece para Cumpli.',
        verify: 'Verificar',
        newPasswordTitle: 'Cambiar contraseña',
        newPassword: 'Nueva contraseña',
        confirmPassword: 'Confirmar contraseña',
        passwordMismatch: 'Las contraseñas no coinciden',
        passwordPolicy: 'Mínimo 12 caracteres, con mayúsculas, minúsculas, números y símbolos.',
        setupMfaTitle: 'Configurar autenticador',
        setupMfaHelp: 'Sigue estos pasos:\n1. Abre tu app de autenticación (Google Authenticator, Authy, Microsoft Authenticator).\n2. Escanea el código QR o ingresa la clave manualmente.\n3. La app mostrará un código de 6 dígitos — ingrésalo abajo para confirmar.',
        secretKey: 'Clave secreta',
        invalidCredentials: 'Correo o contraseña incorrectos',
        invalidCode: 'Código de verificación inválido',
        signOut: 'Cerrar sesión',
        resetTitle: 'Restablecer contraseña',
        resetSend: 'Enviar código',
        resetCode: 'Código de recuperación',
        resetConfirm: 'Restablecer',
        backToLogin: 'Volver a iniciar sesión',
        back: 'Volver',
        signingInAs: 'Entrando como',
        showPassword: 'Mostrar contraseña',
        hidePassword: 'Ocultar contraseña',
        networkError: 'Sin conexión a Internet. Verifique su red e intente de nuevo.',
        resetEmailSent: 'Te enviamos un código a',
        resetEmailNote: 'Revisa tu bandeja de entrada y la carpeta de spam.',
        resetNewPasswordTitle: 'Crea tu nueva contraseña',
        policyMinLength: 'Mínimo 12 caracteres',
        policyUppercase: 'Al menos una mayúscula (A–Z)',
        policyLowercase: 'Al menos una minúscula (a–z)',
        policyNumber: 'Al menos un número (0–9)',
        policySymbol: 'Al menos un símbolo especial (!@#$…)',
        // "Confiar en este dispositivo": remember-device option on the MFA step.
        trustDevice: 'Confiar en este dispositivo',
        trustDeviceHint: 'No te pediremos el código 2FA la próxima vez en este dispositivo.',
        // Reset / recovery flow error + success copy.
        expiredCode: 'El código expiró. Solicita uno nuevo para continuar.',
        tooManyAttempts: 'Demasiados intentos. Espera unos minutos e intenta de nuevo.',
        passwordUpdated: 'Contraseña actualizada. Inicia sesión con tu nueva contraseña.',
    },
    nav: {
        users: 'Usuarios',
        tenants: 'Entidades',
    },
    users: {
        title: 'Usuarios',
        create: 'Crear usuario',
        name: 'Nombre',
        fullName: 'Nombre completo',
        email: 'Correo',
        roles: 'Rol(es)',
        status: 'Estado',
        lastLogin: 'Último acceso',
        active: 'Activo',
        deactivated: 'Desactivado',
        deleted: 'Eliminado',
        all: 'Todos',
        never: 'Nunca',
        searchPlaceholder: 'Buscar por nombre o correo…',
        statusFilter: 'Estado',
        newTitle: 'Crear usuario',
        editTitle: 'Editar usuario',
        deactivate: 'Desactivar usuario',
        deactivateConfirm: '¿Desactivar a {{name}}? Perderá acceso de inmediato.',
        reactivate: 'Reactivar usuario',
        created: 'Usuario creado correctamente',
        updated: 'Usuario actualizado correctamente',
        deactivated_toast: 'Usuario desactivado',
        reactivated_toast: 'Usuario reactivado',
        deleted_toast: 'Usuario eliminado',
        // Delete flow (tenant-admin only, on an already-deactivated user).
        delete: 'Eliminar',
        deleteTitle: 'Eliminar usuario',
        deleteConfirmPrefix: '¿Eliminar a',
        deleteConfirmSuffix: 'de forma permanente? Se le quitará el acceso y el registro quedará marcado como eliminado. Esta acción no se puede deshacer.',
        mustDeactivateFirst: 'Primero debes desactivar al usuario',
        duplicateEmail: 'Ya existe un usuario con ese correo',
        rolesRequired: 'Seleccione al menos un rol',
        invalidEmail: 'Correo inválido',
        nameLength: 'El nombre debe tener entre 2 y 120 caracteres',
        notFoundTitle: 'Usuario no encontrado',
        notFoundHint: 'El usuario no existe o ya no está disponible.',
        loadErrorTitle: 'No se pudo cargar el usuario',
        loadErrorHint: 'Ocurrió un error al consultar el usuario. Vuelve a intentarlo en unos momentos.',
        backToUsers: 'Volver a Usuarios',
    },
    aiLog: {
        title: 'Conversaciones de IA',
        subtitle: 'Registro supervisorio del copiloto: qué se preguntó, qué respondió y qué acciones se confirmaron.',
        tenantLabel: 'Entidad',
        tenantPlaceholder: 'Seleccione la entidad a revisar…',
        empty: 'No hay conversaciones registradas para esta entidad.',
        loading: 'Cargando conversaciones…',
        error: 'No se pudo cargar la bitácora de IA.',
        conversation: 'Conversación',
        turnsOne: 'turno',
        turnsMany: 'turnos',
        question: 'Pregunta',
        answer: 'Respuesta',
        tools: 'Herramientas',
        model: 'Modelo',
        decision: 'Acción confirmada',
        decisionTool: 'Herramienta',
        decisionEntity: 'Entidad afectada',
        decisionEdited: 'Editada antes de confirmar',
        decisionVerbatim: 'Confirmada sin cambios',
        decisionOne: 'acción',
        decisionMany: 'acciones',
        loadMore: 'Cargar más',
        searchPlaceholder: 'Buscar en preguntas y respuestas…',
        from: 'Desde',
        to: 'Hasta',
        userLabel: 'Usuario',
        allUsers: 'Todos los usuarios',
        onlyDecisions: 'Solo con acciones',
        searchingAll: 'Buscando en todo el historial… ({{count}} registros revisados)',
        emptyFiltered: 'Ninguna conversación coincide con los filtros aplicados.',
        partialCoverage: 'La búsqueda cubre solo los primeros {{count}} registros cargados. Use «Cargar más» para ampliar la cobertura.',
        pickOne: 'Seleccione una conversación para revisarla.',
        back: 'Volver a la lista',
    },
    tenants: {
        title: 'Entidades',
        provision: 'Aprovisionar entidad',
        legalName: 'Razón social',
        plan: 'Plan',
        planStandard: 'Estándar',
        planPremium: 'Premium',
        adminSection: 'Administrador de la entidad',
        adminEmail: 'Correo del administrador',
        adminName: 'Nombre del administrador',
        created: 'Entidad creada. Se envió la invitación al administrador.',
        status: 'Estado',
        statusActive: 'Activa',
        statusSuspended: 'Suspendida',
        statusOffboarding: 'Cerrando',
        createdAt: 'Fecha de creación',
        userCount: 'Usuarios',
        emptyTitle: 'Sin entidades',
        emptyHint: 'Aprovisione la primera entidad con el formulario de arriba.',
        suspend: 'Suspender',
        reactivate: 'Reactivar',
        offboard: 'Iniciar cierre',
        changePlan: 'Cambiar plan',
        suspendConfirm: '¿Suspender {{name}}? Sus usuarios perderán acceso mientras la entidad esté suspendida.',
        reactivateConfirm: '¿Reactivar {{name}}? Sus usuarios recuperarán el acceso.',
        offboardConfirm: '¿Iniciar el cierre de {{name}}? La entidad quedará en proceso de salida.',
        updated: 'Entidad actualizada correctamente',
    },
    expedientes: {
        title: 'Expedientes',
        create: 'Nuevo expediente',
        name: 'Nombre',
        docId: 'Cédula / RUC',
        sector: 'Sector',
        kind: 'Tipo',
        email: 'Correo del cliente',
        phone: 'Teléfono del cliente',
        persona: 'Tipo de persona',
        personaNatural: 'Persona Natural',
        personaLegal: 'Persona Jurídica',
        birthDate: 'Fecha de nacimiento',
        estado: 'Estado',
        riesgo: 'Riesgo',
        score: 'Puntaje',
        pep: 'PEP',
        assignedTo: 'Asignado a',
        nextReview: 'Próxima revisión',
        created: 'Expediente creado correctamente',
        updated: 'Expediente actualizado correctamente',
        notFound: 'Expediente no encontrado',
        // Version-history — read the audit trail's before/after states + restore
        // a prior state as a new audited edit (expediente.version_restored).
        versionHistory: 'Historial de versiones',
        restoreVersion: 'Restaurar a esta versión',
        restoreVersionConfirm: 'Se creará una nueva versión con este estado; el historial anterior se conserva.',
        versionRestored: 'Versión restaurada',
    },
    // Redesigned "activity log" — aggregates every audited action on an
    // expediente and its children (documents, revisiones, consentimiento,
    // sujeto, beneficiarios, alertas, screening) into one timeline, plus the
    // restore-focused versioning drawer/panel built on top of it.
    expedienteActivity: {
        title: 'Actividad',
        historyTitle: 'Historial de cambios',
        empty: 'Aún no hay actividad registrada',
        viewDetail: 'Ver detalle',
        // Collapsed-by-default section header (History icon + count Pill + chevron)
        // that reveals the two lanes: Actividad (informational log) and Versiones
        // (restorable version-projection timeline).
        sectionTitle: 'Actividad y versiones',
        // Lane B panel title — the version-projection (expediente_versions) timeline.
        versionsTitle: 'Versiones',
        // Per-row affordance prefix; the UI appends the version's versionSeq, e.g.
        // `${versionLabel} ${entry.versionSeq}` → "Versión 4".
        versionLabel: 'Versión',
        // Pill on each Lane-B row marking it as a live restore point.
        restorableBadge: 'Restaurable',
        // Count-badge suffix for the collapsed header's Pill, e.g.
        // `${total} · ${versionCount} ${versionsCountLabel}` → "12 · 4 versiones".
        versionsCountLabel: 'versiones',
        // Restore-version confirm copy — explicitly warns that the case's estado
        // (incl. approval/escalation) is part of the restored state, in both
        // directions (re-approve or un-approve).
        restoreVersionConfirm: 'Esto también restaurará el estado del expediente, incluida la aprobación.',
        // Extra warning shown alongside restoreVersionConfirm ONLY when the
        // selected version's snapshot shows the expediente in a deleted (papelera)
        // state — restoring it will re-trash an otherwise-active case.
        restoreVersionDeletedWarning: 'Esta versión corresponde al expediente en estado eliminado: restaurarla moverá el expediente a la papelera.',
        // "Ver más" affordance that fetches the next server page of the Versiones
        // lane (distinct from the client-side preview ExpandToggle).
        loadMoreVersions: 'Ver más',
        loadingMoreVersions: 'Cargando…',
        // Drawer that opens on an activity entry to show what changed.
        detail: {
            before: 'Antes',
            after: 'Después',
            changes: 'Cambios',
            noChanges: 'Sin cambios de datos',
            performedBy: 'Realizado por',
            // Two-tab layout on restorable entries: "Cambios" (existing diff view,
            // default) vs "Expediente completo" (full read-only aggregate snapshot
            // fetched from GET /expedientes/{id}/versions/{versionSeq}). Entries
            // with `restorable: false` show only the Cambios view (no tabs).
            tabChanges: 'Cambios',
            tabSnapshot: 'Expediente completo',
            // Heading over the document list rendered from the snapshot's
            // `.documents` array in the "Expediente completo" tab.
            snapshotDocumentsTitle: 'Documentos en esta versión',
            // Muted badge on a snapshot document row whose `deletedAt != null`.
            snapshotDeletedDoc: 'Eliminado',
            // Drawer/tab heading composed client-side with the entry's versionSeq,
            // e.g. previewOfVersion.replace('{{n}}', String(versionSeq)) → "Vista
            // de la Versión 4".
            previewOfVersion: 'Vista de la Versión {{n}}',
            // Field-group headings inside the "Expediente completo" tab — the same
            // grouping the snapshot render uses for the case-aggregate's flat field
            // set (identity/classification, contact, activity/risk).
            snapshotGroupGeneral: 'Datos generales',
            snapshotGroupContact: 'Contacto',
            snapshotGroupRisk: 'Actividad y riesgo',
        },
        // Restore affordances surfaced per entry / in the versioning panel.
        restoreDocument: 'Restaurar documento',
        restoreExpediente: 'Restaurar expediente',
        restoreVersion: 'Restaurar a esta versión',
        restoreDocumentConfirm: 'Se recuperará el documento eliminado; sus archivos se conservan.',
        restoreExpedienteConfirm: 'Se recuperará el expediente eliminado.',
        // Versioning surface (versions + recoverable deleted items).
        versioningTitle: 'Versiones y restauración',
        recoverableItemsTitle: 'Elementos eliminados recuperables',
        recoverableBadge: 'Recuperable',
        // Category label per activity entityType — keyed exactly by the audited
        // entityType strings (expediente, expediente_document, scheduled_review,
        // credit_consent, subject_profile, subject_owner, alert, screening_batch,
        // screening_rescreen, watchlist_refresh) so the UI can map straight from
        // ExpedienteActivityEntry.entityType to a friendly category + icon.
        categories: {
            expediente: 'Expediente',
            expediente_document: 'Documento',
            scheduled_review: 'Revisión',
            credit_consent: 'Consentimiento',
            subject_profile: 'Sujeto',
            subject_owner: 'Beneficiario',
            alert: 'Alerta',
            screening_batch: 'Screening',
            screening_rescreen: 'Screening',
            watchlist_refresh: 'Screening',
        },
    },
    intakeScan: {
        // Intake-wizard id-scan step: upload the cédula, OCR pre-fills the form.
        title: 'Escanea tu cédula',
        help: 'Sube una foto o un PDF de tu cédula y llenaremos los datos por ti.',
        processing: 'Leyendo tu documento…',
        successHint: 'Revisa los datos: puedes corregir cualquier campo antes de continuar.',
        error: 'No pudimos leer el documento. Escribe los datos manualmente.',
        tooLarge: 'El archivo es demasiado grande (máx. 4 MB). Usa una foto o PDF más liviano.',
        skip: 'Prefiero escribirlos yo',
    },
    intake: {
        // Grouped intake screens: one screen confirms all the cédula data at once.
        confirmTitle: 'Confirma los datos de la cédula',
        confirmHelp: 'Ya llenamos los datos por ti. Revisa que coincidan con la cédula y corrige lo que haga falta.',
        datosTitle: '¿Quién es el cliente?',
        datosHelp: 'Escribe los datos como aparecen en la cédula.',
        datosLegalTitle: 'Datos de la empresa',
        datosLegalHelp: 'La razón social y el RUC como aparecen en el registro.',
        nameLabel: 'Nombre y apellidos',
        nameLegalLabel: 'Razón social',
        cedulaLabel: 'Cédula o pasaporte',
        rucLabel: 'RUC',
        actividadHelp: 'Cuéntanos a qué se dedica y su situación.',
        sectorLabel: 'Actividad económica',
        perfilLabel: 'Su situación',
        tipoEntidadLabel: 'Tipo de entidad',
        repsLabel: 'Representantes legales',
        repsHelp: 'Si no sabes, deja en blanco.',
    },
    expedienteDocuments: {
        title: 'Documentos',
        category: 'Categoría',
        name: 'Nombre del documento',
        estado: 'Estado',
        expiryDate: 'Fecha de vencimiento',
        created: 'Documento registrado',
        updated: 'Documento actualizado',
    },
    documentCatalog: {
        searchPlaceholder: 'Buscar por documento o cliente…',
        noResults: 'Ningún documento coincide con tu búsqueda.',
    },
    documentRequests: {
        title: 'Solicitud de documentos',
        channels: 'Canales de envío',
        channelWhatsApp: 'WhatsApp',
        channelEmail: 'Correo electrónico',
        message: 'Mensaje personalizado',
        messagePlaceholder: 'Mensaje que se enviará al cliente (opcional)',
        send: 'Enviar solicitud',
        sent: 'Solicitud enviada correctamente',
        partial: 'Solicitud enviada parcialmente',
        failed: 'Error al enviar la solicitud',
        noPhone: 'El expediente no tiene teléfono registrado para WhatsApp',
        noEmail: 'El expediente no tiene correo registrado para envío',
        defaultMessage: 'Estimado/a {{nombre}}, le solicitamos los documentos requeridos por la regulación AML/CFT (Ley 23 de Panamá). Por favor, presentarlos a la brevedad posible.',
    },
    solicitudDocumentos: {
        // §7 correos automáticos de solicitud de documentos (faltantes / por vencer / vencidos).
        title: 'Solicitud automática de documentos',
        send: 'Solicitar documentos por correo',
        subject: 'Documentos requeridos — expediente {{ref}}',
        noEmail: 'El expediente no tiene correo registrado; no se puede enviar la solicitud',
        nothingToRequest: 'No hay documentos faltantes ni por vencer para este expediente',
        sent: 'Solicitud de documentos enviada por correo',
        // Motivo por documento (single source of truth: DOCUMENT_REQUEST_REASON_LABEL).
        reasonFaltante: 'Falta presentarlo',
        reasonPorVencer: 'Está por vencer',
        reasonVencido: 'Está vencido',
        deadline: '5 días hábiles',
        // Bulk run summary.
        runTitle: 'Envío masivo de solicitudes de documentos',
        revisados: 'Expedientes revisados',
        enviados: 'Correos enviados',
        omitidos: 'Omitidos (sin correo / nada que pedir / envío reciente)',
    },
    alcance: {
        // "Alcance a clientes" — suggested/scheduled/sent client-outreach messages.
        title: 'Alcance a clientes',
        subtitle: 'Genere, revise y envíe mensajes de seguimiento a sus clientes sobre sus documentos.',
        tabSugeridos: 'Sugeridos',
        tabProgramados: 'Programados',
        tabEnviados: 'Enviados',
        generate: 'Generar sugerencias',
        send: 'Enviar ahora',
        schedule: 'Programar',
        cancel: 'Cancelar',
        save: 'Guardar',
        // Estado labels (OUTREACH_STATUSES).
        statusDraft: 'Sugerido',
        statusScheduled: 'Programado',
        statusSending: 'Enviando',
        statusSent: 'Enviado',
        statusFailed: 'Con error',
        statusCanceled: 'Cancelado',
        // Canal labels — same source as documentRequests.channel*.
        channelEmail: 'Correo',
        channelWhatsApp: 'WhatsApp',
        // Motivo labels (OUTREACH_REASONS).
        reasonDocumentoSolicitado: 'Documento solicitado',
        reasonDocumentoPorVencer: 'Documento por vencer',
        reasonDocumentoVencido: 'Documento vencido',
        emptySugeridos: 'No hay sugerencias de alcance por el momento.',
        emptyProgramados: 'No tiene mensajes programados.',
        emptyEnviados: 'Aún no se han enviado mensajes.',
        // Confirm-send dialog.
        confirmSendTitle: '¿Enviar este mensaje ahora?',
        confirmSendBody: 'El mensaje se enviará de inmediato al cliente por los canales seleccionados. Esta acción no se puede deshacer.',
        // Schedule dialog.
        scheduleTitle: 'Programar envío',
        scheduleHelp: 'Elija la fecha y hora en que desea enviar este mensaje al cliente.',
        scheduleDateLabel: 'Fecha y hora de envío',
        schedulePast: 'La fecha y hora ya pasaron. Elija un momento futuro.',
        scheduleSummary: 'Se enviará:',
        // Cumpli-styled date/time picker (schedule dialog).
        pickerPrevMonth: 'Mes anterior',
        pickerNextMonth: 'Mes siguiente',
        pickerTimeLabel: 'Hora de envío',
        presetOneHour: 'En 1 hora',
        presetTomorrowMorning: 'Mañana 9:00 a. m.',
        presetNextWeek: 'En una semana',
        // Toasts.
        generated: 'Se generaron nuevas sugerencias de alcance',
        updated: 'Mensaje actualizado correctamente',
        sent: 'Mensaje enviado correctamente',
        scheduled: 'Mensaje programado correctamente',
        canceled: 'Mensaje cancelado',
        sendError: 'No se pudo enviar el mensaje. Intente de nuevo.',
        saveError: 'No se pudo guardar el mensaje. Intente de nuevo.',
        generateError: 'No se pudieron generar las sugerencias. Intente de nuevo.',
        // Table/card columns + actions.
        colClient: 'Cliente',
        colReason: 'Motivo',
        colChannels: 'Canales',
        colStatus: 'Estado',
        colDate: 'Fecha',
        edit: 'Editar',
        retry: 'Reintentar',
        unschedule: 'Quitar programación',
        reschedule: 'Reprogramar',
        runScheduled: 'Procesar programados',
        runScheduledDone: 'Programados procesados',
        // Edit dialog.
        editTitle: 'Editar mensaje',
        fieldSubject: 'Asunto',
        fieldBody: 'Mensaje',
        fieldChannels: 'Canales de envío',
        channelsRequired: 'Seleccione al menos un canal',
        previewEmail: 'Vista previa · Correo',
        previewWhatsApp: 'Vista previa · WhatsApp',
        // Cancel dialog.
        cancelConfirmTitle: '¿Cancelar este mensaje?',
        cancelConfirmBody: 'El mensaje quedará cancelado y no se enviará al cliente.',
        keepMessage: 'No cancelar',
    },
    entityMode: {
        label: 'Tipo de sujeto obligado',
        financiero: 'Financiero',
        abogados: 'Abogados y gatekeepers',
        valores: 'Casas de valores',
        seguros: 'Aseguradoras',
        inmobiliaria: 'Inmobiliaria',
        help: 'La clase principal de sujeto obligado. Determina qué indicadores aplican y qué subcategorías (plantillas) están disponibles.',
        updated: 'Modo de la entidad actualizado',
        // §2: transactional indicators are bank-only; hidden in modo abogados.
        transactionalNote: 'Los indicadores transaccionales (monto/perfil transaccional, frecuencia e instrumento) aplican solo a entidades financieras.',
    },
    renovacion: {
        // §3 debida-diligencia renewal alarms (cadence 12/24/36 meses por nivel).
        alertType: 'Renovación de debida diligencia',
        title: 'Renovación de debida diligencia',
        due: 'Renovación vencida',
        upcoming: 'Renovación próxima',
        scheduledFor: 'Renovación programada',
        nextReviewAt: 'Próxima revisión',
        cadence: 'Cadencia por nivel',
        empty: 'No hay renovaciones vencidas',
        descriptionDue: 'La debida diligencia de {{nombre}} vence el {{fecha}} (nivel {{nivel}}).',
    },
    screening: {
        // §4 screening recurrente + re-verificación + refresco de listas.
        rescreenTitle: 'Re-verificación de listas',
        rescreenAll: 'Re-verificar toda la base',
        rescreenDue: 'Re-verificar vencidos',
        rescreenBajo: 'Vigilancia continua (bajo riesgo)',
        // §4 alert opened when a re-screen delta raises risk (ok→hit/review, review→hit).
        alertTypeDelta: 'Coincidencia en re-verificación de listas',
        // Motivo recorded in the risk bitácora when a raising delta is flagged.
        deltaMotivo: 'La re-verificación de listas cambió el estado de {{prior}} a {{nuevo}} para {{sujeto}}.',
        // Motivo for the pseudonymised low-risk pipeline (no raw name — uses the pseudonym).
        deltaMotivoBajo: 'La vigilancia continua de bajo riesgo cambió el estado de {{prior}} a {{nuevo}} (sujeto seudonimizado).',
        // §4 alert opened when a re-screen delta LOWERS the status (hit/review→ok): the
        // prior coincidencia ya no aparece. Informational — no cambia el riesgo.
        alertTypeCleared: 'Posible salida de lista — requiere revisión',
        clearedMotivo: 'La re-verificación de listas ya no encuentra la coincidencia previa para {{sujeto}}: el estado bajó de {{prior}} a {{nuevo}}. Requiere confirmación manual; el nivel de riesgo no se modifica automáticamente.',
        // Motivo for the pseudonymised low-risk pipeline (no raw name — uses the pseudonym).
        clearedMotivoBajo: 'La vigilancia continua de bajo riesgo ya no encuentra la coincidencia previa (sujeto seudonimizado): el estado bajó de {{prior}} a {{nuevo}}. Requiere confirmación manual; el nivel de riesgo no se modifica automáticamente.',
        refreshTitle: 'Actualizar listas',
        refreshDone: 'Listas actualizadas',
        empty: 'Sin cambios en la re-verificación',
        // §6 adverse-media watch (news scraper tied to the bitácora).
        adverseMediaTitle: 'Vigilancia de medios adversos',
        adverseMediaAlertType: 'Coincidencia en medios adversos',
        // Motivo recorded in the bitácora + alert when a relevant adverse hit is found.
        // The news itself is the provenance (source_ref carries the URL/title/date).
        adverseMediaMotivo: 'La vigilancia de medios adversos encontró una noticia relevante sobre {{sujeto}}: “{{titulo}}” ({{fuente}}, {{fecha}}). Requiere adjudicación (EDD).',
        // Motivo for the pseudonymised low-risk pipeline (no raw name — uses the pseudonym).
        adverseMediaMotivoBajo: 'La vigilancia continua de bajo riesgo encontró una noticia adversa relevante (sujeto seudonimizado). Requiere adjudicación (EDD).',
        adverseMediaEmpty: 'Sin medios adversos relevantes',
    },
    rp: {
        // Vigilancia registral — barrido (sweep) del estatus de folios en Registro Público.
        sweepTitle: 'Barrido de Registro Público',
        // Alerta abierta cuando el estatus en RP de un cliente cambió desde el último barrido.
        alertTypeChange: 'Cambio en Registro Público',
        cambioMotivo: 'El estatus en Registro Público cambió de {{antes}} a {{despues}}.',
        sinFolio: 'Sin folio de Registro Público',
        empty: 'Sin cambios en Registro Público',
    },
    riskBitacora: {
        title: 'Bitácora de riesgo',
        current: 'Estado actual',
        history: 'Historial',
        empty: 'Sin registros de riesgo',
        nivel: 'Nivel de riesgo',
        motivo: 'Motivo',
        score: 'Puntaje',
        source: 'Origen',
        validFrom: 'Vigente desde',
        validTo: 'Vigente hasta',
        recordedAt: 'Registrado',
        recorded: 'Estado de riesgo registrado',
        // Provenance labels for the 9 source types.
        sourceTypes: {
            matrix_recalc: 'Recálculo de matriz',
            list_hit: 'Coincidencia en lista',
            sanction: 'Sanción',
            pep: 'PEP',
            antecedente: 'Antecedente',
            adverse_media: 'Noticia adversa',
            vigilancia_registral: 'Vigilancia registral',
            manual: 'Manual',
            onboarding: 'Alta del cliente',
        },
    },
    auditoria: {
        title: 'Auditoría',
        downloadPdf: 'Descargar PDF',
        // Friendly es-PA label per AuditAction, for the Auditoría trail UI.
        // Record<AuditAction, string> — TS enforces one label per catalog entry.
        actions: {
            'tenant.created': 'Entidad creada',
            'tenant.updated': 'Entidad actualizada',
            'user.created': 'Usuario creado',
            'user.updated': 'Usuario actualizado',
            'user.roles_assigned': 'Roles asignados',
            'user.deactivated': 'Usuario desactivado',
            'user.reactivated': 'Usuario reactivado',
            'user.deleted': 'Usuario eliminado',
            'auth.login_succeeded': 'Inicio de sesión exitoso',
            'auth.login_failed': 'Inicio de sesión fallido',
            'authz.denied': 'Acceso denegado',
            'validation.ran': 'Validación ejecutada',
            'expediente.created': 'Expediente creado',
            'expediente.updated': 'Expediente actualizado',
            'expediente.deleted': 'Expediente eliminado',
            'expediente.restored': 'Expediente restaurado',
            'expediente.version_restored': 'Versión restaurada',
            'expediente.approved': 'Expediente aprobado',
            'expediente.escalated': 'Expediente escalado',
            'expediente.document.created': 'Documento registrado',
            'expediente.document.updated': 'Documento actualizado',
            'expediente.document.uploaded': 'Documento cargado',
            'expediente.document.deleted': 'Documento eliminado',
            'expediente.document.restored': 'Documento restaurado',
            'expediente.document.replaced': 'Documento reemplazado',
            'subject.created': 'Perfil de sujeto creado',
            'subject.updated': 'Perfil de sujeto actualizado',
            'owner.created': 'Persona relacionada creada',
            'owner.updated': 'Persona relacionada actualizada',
            'declaracion_jurada.created': 'Declaración jurada emitida',
            'expediente.documents_requested': 'Documentos solicitados',
            'alert.updated': 'Alerta actualizada',
            'review.created': 'Revisión programada',
            'review.updated': 'Revisión actualizada',
            'renewal.alert_opened': 'Alerta de renovación abierta',
            'consent.created': 'Consentimiento registrado',
            'consent.updated': 'Consentimiento actualizado',
            'screening.batch.ran': 'Verificación de listas por lote ejecutada',
            'screening.rescreened': 'Re-verificación de listas ejecutada',
            'screening.delta_flagged': 'Coincidencia detectada en re-verificación',
            'screening.cleared_flagged': 'Posible salida de lista detectada',
            'screening.lists_refreshed': 'Listas de sanciones actualizadas',
            'screening.adverse_media_flagged': 'Coincidencia detectada en medios adversos',
            'risk_matrix.updated': 'Matriz de riesgo actualizada',
            'risk_bitacora.recorded': 'Estado de riesgo registrado',
            'tenant.entity_mode_updated': 'Modo de la entidad actualizado',
            'assistant.queried': 'Consulta al asistente de IA',
            'outreach.generated': 'Sugerencias de alcance generadas',
            'outreach.updated': 'Mensaje de alcance actualizado',
            'outreach.sent': 'Mensaje de alcance enviado',
            'outreach.canceled': 'Mensaje de alcance cancelado',
            'registro.login_attempt': 'Intento de inicio de sesión en Registro Público',
            'registro.lote_run': 'Descarga masiva de Registro Público ejecutada',
            'registro.swept': 'Barrido de Registro Público ejecutado',
            'registro.rp_changed': 'Cambio detectado en Registro Público',
            'registro.officers_synced': 'Sincronización de oficiales ejecutada',
        },
    },
    assistant: {
        proposedAction: 'Acción propuesta',
        confirm: 'Confirmar',
        cancel: 'Cancelar',
        executed: 'Acción ejecutada',
        cancelled: 'Acción cancelada',
        reviewBeforeConfirm: 'El asistente propone ejecutar esta acción. Revísala antes de confirmar.',
        shortcutHint: 'Abrir asistente (Ctrl+K / ⌘K)',
    },
    // Fase 2 — labels de tipos de documento (espejo de DOC_TYPE_LABELS en required-docs.ts;
    // mantener en sincronía, un test lo verifica).
    docTypes: {
        cedula_pasaporte: 'Cédula o pasaporte',
        comprobante_domicilio: 'Comprobante de domicilio',
        carta_referencia_bancaria: 'Carta de referencia bancaria (indica producto vigente o cliente valioso)',
        carta_referencia_profesional: 'Carta de referencia profesional',
        declaracion_pep: 'Declaración de persona expuesta políticamente (PEP)',
        fuente_fondos: 'Declaración de fuente de fondos',
        pacto_social: 'Pacto social',
        certificado_registro_publico: 'Certificado del Registro Público',
        ruc_aviso_operacion: 'RUC / Aviso de Operación',
        registro_accionistas: 'Registro de accionistas',
        poder_acta_junta: 'Poder o acta de junta directiva',
        organigrama_estructura: 'Organigrama de la estructura de propiedad',
        estados_financieros: 'Estados financieros',
        formulario_kyc_firmado: 'Formulario "Conozca a su Cliente" firmado',
        certificados_acciones: 'Certificados de acciones',
        acta_fundacional: 'Acta fundacional',
        declaracion_jurada_bf: 'Declaración jurada de beneficiario final',
        fatca_crs: 'Autocertificación FATCA/CRS (W-8/W-9)',
        perfil_idoneidad: 'Perfil de idoneidad (suitability)',
        datos_poliza: 'Datos de la póliza',
        datos_inmueble: 'Datos del inmueble',
        forma_pago: 'Forma de pago',
    },
};
//# sourceMappingURL=es-PA.js.map